import { createC2pa } from '@contentauth/c2pa-web';

let c2paInstance = null;
let c2paInitializationPromise = null;

/**
 * Initializes the C2PA WASM instance if not already initialized.
 * Ensures only one initialization occurs concurrently.
 */
async function getC2paInstance() {
    if (c2paInstance) return c2paInstance;

    if (!c2paInitializationPromise) {
        c2paInitializationPromise = createC2pa({
            // Path must match where we copied it in the postinstall script
            wasmSrc: process.env.PUBLIC_URL + '/c2pa_bg.wasm',
            workerSrc: null, // We aren't using a separate worker script here for simplicity unless required
        }).then(instance => {
            c2paInstance = instance;
            return instance;
        }).catch(err => {
            console.error('Failed to initialize C2PA:', err);
            c2paInitializationPromise = null;
            return null;
        });
    }

    return c2paInitializationPromise;
}

/**
 * Reads C2PA metadata from a File.
 * @param {File} file 
 * @returns {Object|null} Formatted C2PA data or null if none/error
 */
export async function readC2pa(file) {
    try {
        const c2pa = await getC2paInstance();
        if (!c2pa) return null;

        // Use the correct v0.6.1 reader factory API instead of c2pa.read()
        const reader = await c2pa.reader.fromBlob(file.type || 'image/png', file);
        if (!reader) return null;

        const manifestStore = await reader.manifestStore();
        const active = await reader.activeManifest();

        if (!manifestStore || !active) {
            return null;
        }

        const c2paData = {
            title: active.title,
            format: active.format,
            claimGenerator: active.claimGenerator || (active.claim_generator_info ? active.claim_generator_info.map(i => i.name).join(', ') : null) || (active.claimGeneratorInfo ? active.claimGeneratorInfo.map(i => i.name).join(', ') : null),
            signatureIssuer: active.signatureInfo?.issuer || active.signature_info?.issuer,
            signatureTime: active.signatureInfo?.time || active.signature_info?.time,
            ingredients: active.ingredients?.map(ing => ({
                title: ing.title,
                format: ing.format,
                relationship: ing.relationship,
            })) || [],
            assertions: active.assertions?.data || active.assertions || [],
        };

        // Try to determine if AI generated based on Content Credentials
        let isAIGenerated = false;
        let aiGenerator = null;

        // Check claimGenerator and signatureIssuer for explicit AI brands
        const generatorLower = `${c2paData.claimGenerator || ''} ${c2paData.signatureIssuer || ''}`.toLowerCase();
        if (
            generatorLower.includes('dall-e') ||
            generatorLower.includes('midjourney') ||
            generatorLower.includes('firefly') ||
            generatorLower.includes('stable diffusion') ||
            generatorLower.includes('bing image creator') ||
            generatorLower.includes('chatgpt') ||
            generatorLower.includes('openai')
        ) {
            isAIGenerated = true;
            aiGenerator = (c2paData.claimGenerator || c2paData.signatureIssuer || '').split('-')[0].trim(); // Rough extraction
        }

        // Check assertions (actions like c2pa.created)
        for (const assertion of c2paData.assertions) {
            if (assertion.label === 'c2pa.actions' && assertion.data?.actions) {
                for (const action of assertion.data.actions) {
                    const sw = (action.softwareAgent || '').toLowerCase();
                    if ((sw.includes('firefly') || sw.includes('adobe')) && action.action === 'c2pa.created') {
                        isAIGenerated = true;
                        if (!aiGenerator) aiGenerator = action.softwareAgent;
                    }
                    if (action.action.includes('c2pa.created') && (sw.includes('ai ') || sw.includes(' dnn') || sw.includes('diffusion') || sw.includes('midjourney'))) {
                        isAIGenerated = true;
                        if (!aiGenerator) aiGenerator = action.softwareAgent;
                    }
                }
            }
        }

        return {
            manifest: c2paData,
            rawStore: manifestStore, // Kept for debugging/tree view
            aiDetection: {
                isAIGenerated,
                aiGenerator,
                confidence: isAIGenerated ? 1.0 : 0 // Hardware-signed C2PA assertions have 100% confidence
            }
        };

    } catch (err) {
        console.warn('C2PA reading failed or not present:', err);
        return null;
    }
}
